import { DashboardRegistryRow } from "../constants/interface/dashboardRegistry";
import { SearchableDashboard } from "../constants/searchableDashboards";
import { DASHBOARD_REGISTRY } from "../constants/dashboardRegistry";
import { fetchDataV2, patchDataV2, postDataV2 } from "./utils";

// GET /dashboards is live (docs/needed-endpoints.md §1) - pre-filtering was dropped in favor of
// always returning every active dashboard with a per-caller `has_access` flag, so every consumer
// reads through useDashboardRegistry() and decides its own filtering (nav-type surfaces filter to
// has_access, the Dashboards page and Settings' access section show/act on the gaps).
//
// is_active now doubles as "archived" (product decision - backend change owned by the backend
// team, not this repo): is_active=false means the dashboard is archived (still fully viewable at
// its own real route - see ArchivedDashboardCard.tsx - just kept out of the normal
// browse/search/pick surfaces), NOT "doesn't exist" the way it used to.
//
// The endpoint also takes an `is_active` query param (backend team, confirmed): omitted -> every
// dashboard, active and archived; `true`/`false` -> just one or the other, filtered server-side.
// Deliberately never passed here - this is the one shared query every dashboard-aware page reads
// through (useDashboardRegistry(), staleTime: Infinity), so calling it with a specific `is_active`
// value would either need a second, separately-cached query just for ArchivedPage.tsx (undoing the
// "one shared fetch" the whole point of this hook), or force every other consumer to also filter
// server-side instead of the one client-side `dashboards.filter(...)` line they already have. Every
// consumer that only wants active dashboards (Dashboards page, Home search, the Downloads page's
// dashboard picker, Favourites) filters `is_active` explicitly itself instead.
export const getDashboardRegistry = (): Promise<SearchableDashboard[]> =>
  fetchDataV2("/dashboards").then((response) => (Array.isArray(response) ? response : []));

// Mock of the future `_dashboard` registry table (see docs/dashboard-registry-seed-checklist.md),
// scoped to just the columns PrivateRoute's access-control lookup needs. Same "wrap in a Promise,
// swap the body later" pattern as getDashboardRegistry() above.
export const getDashboardRegistryRows = (): Promise<DashboardRegistryRow[]> => Promise.resolve(DASHBOARD_REGISTRY);

// Raw create/update shape for the dashboard row itself (docs/needed-endpoints.md §11) - distinct
// from SearchableDashboard (§1's GET /dashboards row), which additionally carries categories/tags/
// matched_terms/has_access that don't apply to authoring a dashboard record. No frontend UI creates
// or edits a dashboard from scratch yet (the Dashboards page's "Add Dashboard" action only assigns
// an *existing* dashboard to a category - see dashboardCategoriesApi.ts's assignDashboardToCategory)
// - wired ahead of time matching this codebase's usual "wrap it now, build the UI later" pattern.
export interface DashboardRecord {
  id: number;
  name: string;
  key: string;
  internal_view: string;
  description: string;
  path: string;
  icon: string;
  hide_on_mobile: boolean;
  supports_export: boolean;
  engagement_url: string;
  is_active: boolean;
}

export const createDashboard = (payload: Omit<DashboardRecord, "id">): Promise<DashboardRecord> => postDataV2("/admin/dashboards", payload);

export const updateDashboard = (dashboardId: number, payload: Partial<Omit<DashboardRecord, "id">>): Promise<DashboardRecord> =>
  patchDataV2(`/admin/dashboards/${dashboardId}`, payload);
